import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Coins, Lightning, Infinity as InfinityIcon, CheckCircle, Sparkle, ArrowRight, Minus, Plus, CircleNotch,
} from "@phosphor-icons/react";

const PLANS = [
  { id: "plan_starter_monthly",   name: "Base",  price: 18,   credits: 7,          per: "credits/mo", desc: "For solo techs putting a few cases through the AI each week." },
  { id: "plan_pro_monthly",       name: "Pro",   price: 55,   credits: 30,         per: "credits/mo", highlight: true, desc: "For busy technicians running the AI on most walk-ins." },
  { id: "plan_unlimited_monthly", name: "Shop",  price: 150,  credits: Infinity,   per: "unlimited",  desc: "For shops with multiple bays. No cap, no counting." },
];

// Plan tier rank — must match backend PLAN_META rank
const PLAN_RANK = {
  plan_starter_monthly: 1,
  plan_pro_monthly: 2,
  plan_unlimited_monthly: 3,
};

export default function Billing() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(null); // lookup_key currently loading
  const [changingTo, setChangingTo] = useState(null);      // lookup_key of in-flight plan swap
  const [qty, setQty] = useState(1);
  const [polling, setPolling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const pollTimer = useRef(null);

  const loadCredits = async () => {
    try {
      const [{ data }, subRes] = await Promise.all([
        api.get("/credits"),
        api.get("/subscription").catch(() => ({ data: { active: false } })),
      ]);
      setCredits(data);
      setSub(subRes.data);
    } catch (err) {
      console.error("Failed to load billing state:", err);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCredits(); return () => { if (pollTimer.current) clearInterval(pollTimer.current); }; }, []);

  // If we returned from Stripe with ?session_id=…, poll until paid
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    pollTimer.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          clearInterval(pollTimer.current);
          setPolling(false);
          toast.success("Payment confirmed — credits added!");
          loadCredits();
          navigate("/billing", { replace: true });
        } else if (attempts > 30) {
          clearInterval(pollTimer.current);
          setPolling(false);
          toast.error("Still waiting on Stripe. Refresh in a moment.");
        }
      } catch (err) {
        console.error("Payment status poll failed:", err);
      }
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async (lookup_key, quantity = 1) => {
    setCheckoutBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key, quantity, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setCheckoutBusy(null);
    }
  };

  const changePlan = async (lookup_key, planName, isUpgrade) => {
    const verb = isUpgrade ? "Upgrade" : "Switch";
    const msg = isUpgrade
      ? `Upgrade to ${planName}? Stripe will charge the prorated difference now and you'll get the new plan's credits immediately.`
      : `Switch to ${planName}? Stripe will credit the unused portion of your current plan to your next invoice.`;
    if (!confirm(msg)) return;
    setChangingTo(lookup_key);
    try {
      const { data } = await api.post("/subscription/change", { lookup_key });
      toast.success(`${verb} complete — you're on the ${planName} plan.`);
      loadCredits();
      // eslint-disable-next-line no-unused-vars
      const _ = data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    } finally {
      setChangingTo(null);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-xs font-mono-tech text-muted-foreground uppercase tracking-widest">// billing</div>
        <h1 className="font-display font-bold text-5xl tracking-tight">CREDITS &amp; PLANS</h1>
        <p className="text-muted-foreground mt-2">
          Every diagnostic session costs <span className="text-white/90">1 credit</span>. Top up any time or subscribe to a plan.
        </p>

        {polling && (
          <div className="mt-6 border border-primary/40 bg-primary/5 rounded-md p-4 flex items-center gap-3" data-testid="polling-banner">
            <CircleNotch weight="bold" className="w-5 h-5 text-primary animate-spin" />
            <div>
              <div className="font-display text-lg">Confirming payment…</div>
              <div className="text-xs text-muted-foreground font-mono-tech">Waiting on Stripe · usually a few seconds</div>
            </div>
          </div>
        )}

        {/* Balance card */}
        <div className="mt-8 border border-border rounded-md p-6 bg-card flex flex-wrap items-center justify-between gap-6" data-testid="balance-card">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center">
              {credits?.plan_unlimited
                ? <InfinityIcon weight="bold" className="w-7 h-7 text-primary" />
                : <Coins weight="fill" className="w-7 h-7 text-primary" />}
            </div>
            <div>
              <div className="text-[10px] font-mono-tech text-muted-foreground uppercase tracking-widest">Available</div>
              {loading ? (
                <div className="font-display text-3xl">…</div>
              ) : credits?.plan_unlimited ? (
                <div className="font-display text-4xl font-bold text-primary">UNLIMITED</div>
              ) : (
                <div className="font-display text-4xl font-bold" data-testid="credit-balance">
                  {(credits?.monthly_remaining ?? 0) + (credits?.balance ?? 0)}
                  <span className="text-lg text-muted-foreground ml-2">credits</span>
                </div>
              )}
            </div>
          </div>
          <div className="text-sm text-muted-foreground text-right">
            {credits?.plan_name && !credits.plan_unlimited && (
              <div><span className="text-white/90 font-mono-tech">{credits.plan_name}</span> · {credits.monthly_remaining}/{credits.plan_monthly_credits} this cycle</div>
            )}
            {credits?.plan_name && (
              <div className="text-xs mt-1 font-mono-tech">
                {credits.plan_cycle_end ? `Renews ${new Date(credits.plan_cycle_end).toLocaleDateString()}` : ""}
              </div>
            )}
            {!credits?.plan_name && (
              <div className="text-xs">No active subscription · pay-as-you-go</div>
            )}
            <div className="text-[10px] text-muted-foreground mt-2 font-mono-tech uppercase tracking-widest">
              Lifetime used: {credits?.lifetime_used ?? 0}
            </div>
          </div>
        </div>

        {/* Manage subscription — cancel / resume */}
        {sub?.active && (
          <div className="mt-4 border border-border rounded-md p-4 bg-card flex flex-wrap items-center justify-between gap-3" data-testid="manage-sub-card">
            <div className="text-sm">
              <div className="text-white/90">
                Subscription status: <span className={`font-mono-tech uppercase ${sub.cancel_at_period_end ? "text-yellow-400" : "text-green-400"}`}>
                  {sub.cancel_at_period_end ? "CANCELLING" : sub.status.toUpperCase()}
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 font-mono-tech">
                {sub.cancel_at_period_end
                  ? (sub.current_period_end ? `Access ends ${new Date(sub.current_period_end).toLocaleDateString()}` : "Cancellation scheduled")
                  : (sub.current_period_end ? `Next renewal ${new Date(sub.current_period_end).toLocaleDateString()}` : "Active plan")}
              </div>
            </div>
            {sub.cancel_at_period_end ? (
              <Button
                variant="outline"
                onClick={async () => {
                  setCancelling(true);
                  try { await api.post("/subscription/resume"); toast.success("Subscription resumed"); loadCredits(); }
                  catch (e) { toast.error(e?.response?.data?.detail || "Resume failed"); }
                  finally { setCancelling(false); }
                }}
                disabled={cancelling}
                data-testid="resume-sub-btn"
              >
                Resume subscription
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={async () => {
                  if (!confirm("Cancel at end of billing period? You'll keep access until renewal.")) return;
                  setCancelling(true);
                  try { await api.post("/subscription/cancel"); toast.success("Subscription will cancel at period end"); loadCredits(); }
                  catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
                  finally { setCancelling(false); }
                }}
                disabled={cancelling}
                data-testid="cancel-sub-btn"
              >
                Cancel plan
              </Button>
            )}
          </div>
        )}

        {/* One-time top-up */}
        <div className="mt-8 border border-border rounded-md p-6 bg-card" data-testid="topup-card">
          <div className="flex items-center gap-2 mb-1">
            <Sparkle weight="fill" className="w-4 h-4 text-primary" />
            <div className="font-mono-tech text-xs uppercase tracking-widest text-muted-foreground">// pay-as-you-go</div>
          </div>
          <div className="font-display text-2xl tracking-tight">Buy diagnostic credits</div>
          <div className="text-sm text-muted-foreground mt-1">
            <span className="font-mono-tech text-white/90">$10</span> per credit · never expires · use whenever.
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center border border-border rounded-md overflow-hidden">
              <button
                type="button"
                onClick={() => setQty((n) => Math.max(1, n - 1))}
                className="w-10 h-12 hover:bg-secondary text-muted-foreground"
                data-testid="qty-decrement"
              ><Minus weight="bold" className="w-4 h-4 mx-auto" /></button>
              <input
                type="number" min="1" max="100"
                value={qty}
                onChange={(e) => setQty(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
                className="w-16 h-12 bg-transparent text-center font-mono-tech font-semibold text-lg outline-none"
                data-testid="qty-input"
              />
              <button
                type="button"
                onClick={() => setQty((n) => Math.min(100, n + 1))}
                className="w-10 h-12 hover:bg-secondary text-muted-foreground"
                data-testid="qty-increment"
              ><Plus weight="bold" className="w-4 h-4 mx-auto" /></button>
            </div>
            <div className="font-display text-2xl">= <span className="text-primary font-bold">${qty * 10}</span></div>
            <Button
              onClick={() => startCheckout("credit_single", qty)}
              disabled={checkoutBusy === "credit_single"}
              className="min-h-12 bg-primary hover:bg-primary/90 flex-1 md:flex-none"
              data-testid="checkout-credits-btn"
            >
              <Coins weight="fill" className="w-4 h-4 mr-2" />
              {checkoutBusy === "credit_single" ? "Redirecting…" : `Buy ${qty} credit${qty > 1 ? "s" : ""}`}
              <ArrowRight weight="bold" className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Monthly plans */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-1">
            <Lightning weight="fill" className="w-4 h-4 text-primary" />
            <div className="font-mono-tech text-xs uppercase tracking-widest text-muted-foreground">// monthly plans</div>
          </div>
          <div className="font-display text-2xl tracking-tight mb-4">Or pick a plan and stop counting</div>
          <div className="grid md:grid-cols-3 gap-4">
            {PLANS.map((plan) => {
              const isActive = credits?.plan === plan.id;
              const hasActiveSub = !!sub?.active && !!credits?.plan;
              const currentRank = PLAN_RANK[credits?.plan] || 0;
              const thisRank = PLAN_RANK[plan.id] || 0;
              const isUpgrade = hasActiveSub && thisRank > currentRank;
              const isDowngrade = hasActiveSub && thisRank < currentRank;
              const swapLabel = isUpgrade ? "Upgrade" : isDowngrade ? "Switch plan" : "Subscribe";
              const swapBusy = changingTo === plan.id;
              return (
                <div
                  key={plan.id}
                  className={`border rounded-md p-6 bg-card relative transition-colors ${
                    plan.highlight ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]" : "border-border"
                  } ${isActive ? "ring-2 ring-primary/50" : ""}`}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.highlight && (
                    <span className="absolute -top-3 left-6 text-[10px] font-mono-tech uppercase tracking-widest px-2 py-1 bg-primary text-white rounded-md">
                      Most popular
                    </span>
                  )}
                  {isActive && (
                    <span className="absolute -top-3 right-6 text-[10px] font-mono-tech uppercase tracking-widest px-2 py-1 bg-green-500/20 border border-green-500/40 text-green-400 rounded-md">
                      Active
                    </span>
                  )}
                  <div className="font-display text-2xl tracking-tight">{plan.name}</div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <div className="font-display font-bold text-5xl tracking-tight">${plan.price}</div>
                    <div className="text-sm text-muted-foreground">/mo</div>
                  </div>
                  <div className="mt-2 font-mono-tech text-sm text-primary">
                    {plan.credits === Infinity ? "Unlimited sessions" : `${plan.credits} ${plan.per}`}
                  </div>
                  <div className="text-sm text-muted-foreground mt-3 leading-relaxed">{plan.desc}</div>
                  <ul className="mt-4 space-y-1 text-sm">
                    {[
                      plan.credits === Infinity ? "Unlimited diagnostic sessions" : `${plan.credits} sessions every month`,
                      "Cancel any time",
                      "Rolls over to the next month at renewal",
                      "Google Shopping OEM lookup included",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2 text-white/85">
                        <CheckCircle weight="fill" className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {isActive ? (
                    <Button
                      disabled
                      className="mt-5 w-full min-h-12"
                      variant="outline"
                      data-testid={`subscribe-${plan.id}`}
                    >
                      Current plan
                    </Button>
                  ) : hasActiveSub ? (
                    <Button
                      onClick={() => changePlan(plan.id, plan.name, isUpgrade)}
                      disabled={swapBusy}
                      className={`mt-5 w-full min-h-12 ${isUpgrade ? "bg-primary hover:bg-primary/90" : ""}`}
                      variant={isUpgrade ? "default" : "outline"}
                      data-testid={`change-${plan.id}`}
                    >
                      {swapBusy
                        ? "Working…"
                        : <>{swapLabel} <ArrowRight weight="bold" className="w-4 h-4 ml-2" /></>}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => startCheckout(plan.id, 1)}
                      disabled={checkoutBusy === plan.id}
                      className={`mt-5 w-full min-h-12 ${plan.highlight ? "bg-primary hover:bg-primary/90" : ""}`}
                      variant={plan.highlight ? "default" : "outline"}
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {checkoutBusy === plan.id
                        ? "Redirecting…"
                        : <>Subscribe <ArrowRight weight="bold" className="w-4 h-4 ml-2" /></>}
                    </Button>
                  )}
                  {hasActiveSub && !isActive && (
                    <div className="mt-2 text-[11px] font-mono-tech text-muted-foreground text-center">
                      {isUpgrade ? "Stripe prorates the difference — charged now" : "Unused credit applied to next invoice"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
